// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Jotai from "jotai";
import * as React from "react";
import * as Core__Option from "@rescript/core/src/Core__Option.bs.js";
import * as Utils$OrcaPaymentPage from "../Utilities/Utils.bs.js";
import * as JotaiAtoms$OrcaPaymentPage from "../Utilities/JotaiAtoms.bs.js";
import * as PaymentEventData$OrcaPaymentPage from "../../shared-code/sdk-utils/events/PaymentEventData.bs.js";
import * as SubscriptionEventTypes$OrcaPaymentPage from "../Types/SubscriptionEventTypes.bs.js";

function useSubscriptionEventEmitter() {
  let options = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.optionAtom);
  let subscribedEvents = options.subscriptionEvents;
  let emitCardInfo = (elementTypeOpt, cardInfo) => {
    let elementType = elementTypeOpt !== undefined ? elementTypeOpt : "payment";
    if (PaymentEventData$OrcaPaymentPage.shouldEmitEvent("CardDetailsChange", Core__Option.getOr(subscribedEvents, []))) {
      return Utils$OrcaPaymentPage.messageParentWindow(undefined, SubscriptionEventTypes$OrcaPaymentPage.createCardInfoPayload(elementType, cardInfo));
    }
  };
  let emitPaymentMethodStatus = (paymentMethod, paymentMethodType, isSavedPaymentMethod, isOneClickWalletOpt) => {
    let isOneClickWallet = isOneClickWalletOpt !== undefined ? isOneClickWalletOpt : false;
    if (PaymentEventData$OrcaPaymentPage.shouldEmitEvent("PaymentMethodChange", Core__Option.getOr(subscribedEvents, []))) {
      return Utils$OrcaPaymentPage.messageParentWindow(undefined, SubscriptionEventTypes$OrcaPaymentPage.createPaymentMethodStatusPayload(paymentMethod, paymentMethodType, isSavedPaymentMethod, isOneClickWallet));
    }
  };
  let emitBillingAddress = (country, state, postalCode) => {
    if (PaymentEventData$OrcaPaymentPage.shouldEmitEvent("BillingDetailsChange", Core__Option.getOr(subscribedEvents, []))) {
      return Utils$OrcaPaymentPage.messageParentWindow(undefined, SubscriptionEventTypes$OrcaPaymentPage.createBillingAddressPayload(country, state, postalCode));
    }
  };
  let emitCvcStatus = (iframeId, isCvcEmpty, isCvcComplete) => {
    if (PaymentEventData$OrcaPaymentPage.shouldEmitEvent("CvcStatusChange", Core__Option.getOr(subscribedEvents, []))) {
      return Utils$OrcaPaymentPage.messageParentWindow(undefined, SubscriptionEventTypes$OrcaPaymentPage.createCvcStatusPayload(iframeId, isCvcEmpty, isCvcComplete));
    }
  };
  let emitSurcharge = surchargeDetails => {
    if (PaymentEventData$OrcaPaymentPage.shouldEmitEvent("SurchargeInfo", Core__Option.getOr(subscribedEvents, [])) && Core__Option.isSome(surchargeDetails)) {
      return Utils$OrcaPaymentPage.messageParentWindow(undefined, SubscriptionEventTypes$OrcaPaymentPage.createSurchargePayload(surchargeDetails));
    }
  };
  let emitOffers = offerDetails => {
    if (PaymentEventData$OrcaPaymentPage.shouldEmitEvent("AppliedOffersInfo", Core__Option.getOr(subscribedEvents, [])) && Core__Option.isSome(offerDetails)) {
      return Utils$OrcaPaymentPage.messageParentWindow(undefined, SubscriptionEventTypes$OrcaPaymentPage.createAppliedOffersPayload(offerDetails));
    }
  };
  return {
    emitCardInfo: emitCardInfo,
    emitPaymentMethodStatus: emitPaymentMethodStatus,
    emitBillingAddress: emitBillingAddress,
    emitCvcStatus: emitCvcStatus,
    emitSurcharge: emitSurcharge,
    emitOffers: emitOffers
  };
}

function emitReady(iframeId, elementType) {
  Utils$OrcaPaymentPage.messageParentWindow(undefined, [
    [
      "ready",
      true
    ],
    [
      "elementType",
      elementType
    ],
    [
      "iframeId",
      iframeId
    ]
  ]);
}

function useEmitFormStatus(empty, complete, isOneClickWalletOpt, enabledOpt) {
  let isOneClickWallet = isOneClickWalletOpt !== undefined ? isOneClickWalletOpt : false;
  let enabled = enabledOpt !== undefined ? enabledOpt : true;
  let options = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.optionAtom);
  let subscribedEvents = options.subscriptionEvents;
  React.useEffect(() => {
    if (enabled && !isOneClickWallet) {
      let formStatusValue = PaymentEventData$OrcaPaymentPage.computeFormStatus(complete, empty);
      if (PaymentEventData$OrcaPaymentPage.shouldEmitEvent("FormStatusChange", Core__Option.getOr(subscribedEvents, []))) {
        Utils$OrcaPaymentPage.messageParentWindow(undefined, SubscriptionEventTypes$OrcaPaymentPage.createFormStatusPayload(formStatusValue));
      }
    }
  }, [
    empty,
    complete,
    isOneClickWallet,
    subscribedEvents,
    enabled
  ]);
}

function useEmitBillingAddress() {
  let country = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.userCountry);
  let state = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.userAddressState).value;
  let pinCode = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.userAddressPincode).value;
  let options = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.optionAtom);
  let subscribedEvents = options.subscriptionEvents;
  React.useEffect(() => {
    if (PaymentEventData$OrcaPaymentPage.shouldEmitEvent("BillingDetailsChange", Core__Option.getOr(subscribedEvents, []))) {
      Utils$OrcaPaymentPage.messageParentWindow(undefined, SubscriptionEventTypes$OrcaPaymentPage.createBillingAddressPayload(country, state, pinCode));
    }
  }, [
    country,
    state,
    pinCode,
    subscribedEvents
  ]);
}

function getPaymentMethodAndType(paymentMethodName, paymentMethods, logger) {
  if (paymentMethodName.includes("_debit")) {
    return [
      "bank_debit",
      paymentMethodName
    ];
  }
  if (paymentMethodName.includes("_transfer")) {
    return [
      "bank_transfer",
      paymentMethodName
    ];
  }
  if (paymentMethodName === "card") {
    return [
      "card",
      "debit"
    ];
  }
  let found = paymentMethods.filter(pm => pm.payment_method_types.filter(t => t.payment_method_type === paymentMethodName).length > 0)[0];
  if (found !== undefined) {
    return [
      found.payment_method,
      paymentMethodName
    ];
  } else {
    logger.setLogError("Payment method type not found", "PAYMENT_METHOD_TYPE_DETECTION_FAILED", undefined, undefined, undefined, undefined, undefined);
    return;
  }
}

function useEmitPaymentMethodStatus(paymentMethodName, paymentMethods, isSavedPaymentMethod, isOneClickWallet) {
  let loggerState = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.loggerAtom);
  let options = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.optionAtom);
  let subscribedEvents = options.subscriptionEvents;
  React.useEffect(() => {
    if (PaymentEventData$OrcaPaymentPage.shouldEmitEvent("PaymentMethodChange", Core__Option.getOr(subscribedEvents, []))) {
      let match = getPaymentMethodAndType(paymentMethodName, paymentMethods, loggerState);
      if (match !== undefined) {
        Utils$OrcaPaymentPage.messageParentWindow(undefined, SubscriptionEventTypes$OrcaPaymentPage.createPaymentMethodStatusPayload(match[0], match[1], isSavedPaymentMethod, isOneClickWallet));
      }
    }
  }, [
    paymentMethodName,
    paymentMethods,
    isSavedPaymentMethod,
    isOneClickWallet,
    subscribedEvents
  ]);
}

function useEmitSurchargeInfo(surchargeDetails) {
  let options = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.optionAtom);
  let subscribedEvents = options.subscriptionEvents;
  React.useEffect(() => {
    if (PaymentEventData$OrcaPaymentPage.shouldEmitEvent("SurchargeInfo", Core__Option.getOr(subscribedEvents, [])) && Core__Option.isSome(surchargeDetails)) {
      Utils$OrcaPaymentPage.messageParentWindow(undefined, SubscriptionEventTypes$OrcaPaymentPage.createSurchargePayload(surchargeDetails));
    }
  }, [
    surchargeDetails,
    subscribedEvents
  ]);
}

function useEmitAppliedOffersInfo(offerDetails) {
  let options = Jotai.useAtomValue(JotaiAtoms$OrcaPaymentPage.optionAtom);
  let subscribedEvents = options.subscriptionEvents;
  React.useEffect(() => {
    if (PaymentEventData$OrcaPaymentPage.shouldEmitEvent("AppliedOffersInfo", Core__Option.getOr(subscribedEvents, [])) && Core__Option.isSome(offerDetails)) {
      Utils$OrcaPaymentPage.messageParentWindow(undefined, SubscriptionEventTypes$OrcaPaymentPage.createAppliedOffersPayload(offerDetails));
    }
  }, [
    offerDetails,
    subscribedEvents
  ]);
}

export {
  useSubscriptionEventEmitter,
  emitReady,
  useEmitFormStatus,
  useEmitBillingAddress,
  getPaymentMethodAndType,
  useEmitPaymentMethodStatus,
  useEmitSurchargeInfo,
  useEmitAppliedOffersInfo,
}
/* jotai Not a pure module */
